import React, { useEffect, useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '../components/ui/card'
import { Button } from '../components/ui/button'
import axios from '../utils/api'
import { Gift, Package } from 'lucide-react'

export default function StorePage() {
  const [gifts, setGifts] = useState([])
  const [userPoints, setUserPoints] = useState(0)
  const [loading, setLoading] = useState(true)
  const [selectedGift, setSelectedGift] = useState(null)
  const [shippingAddress, setShippingAddress] = useState('')
  const [redeeming, setRedeeming] = useState(false)

  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      try {
        const auth = JSON.parse(localStorage.getItem('auth'))
        const [giftsRes, profileRes] = await Promise.all([
          axios.get('/user/gifts', {
            headers: { Authorization: `Bearer ${auth.token}` }
          }),
          axios.get('/user/dashboard', {
            headers: { Authorization: `Bearer ${auth.token}` }
          })
        ])
        
        if (cancelled) return
        
        setGifts(giftsRes.data)
        setUserPoints(profileRes.data.user.totalPoints)
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchData()
    return () => { cancelled = true }
  }, [])

  const fetchGifts = async () => {
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const response = await axios.get('/user/gifts', {
        headers: { Authorization: `Bearer ${auth.token}` }
      })
      setGifts(response.data)
    } catch (error) {
      console.error('Error fetching gifts:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchUserProfile = async () => {
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const response = await axios.get('/user/dashboard', {
        headers: { Authorization: `Bearer ${auth.token}` }
      })
      setUserPoints(response.data.user.totalPoints)
    } catch (error) {
      console.error('Error fetching user profile:', error)
    }
  }

  const handleRedeemClick = (gift) => {
    setSelectedGift(gift)
  }

  const handleRedeem = async () => {
    if (!shippingAddress.trim()) {
      alert('Please enter a shipping address')
      return
    }

    setRedeeming(true)
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      await axios.post(
        '/user/redeem',
        {
          giftId: selectedGift.id,
          shippingAddress: shippingAddress
        },
        {
          headers: { Authorization: `Bearer ${auth.token}` }
        }
      )
      alert('Gift redeemed successfully! You will receive it soon.')
      setSelectedGift(null)
      setShippingAddress('')
      fetchGifts()
      fetchUserProfile()
    } catch (error) {
      console.error('Error redeeming gift:', error)
      alert(error.response?.data?.message || 'Failed to redeem gift')
    } finally {
      setRedeeming(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Gift className="h-6 w-6 sm:h-8 sm:w-8" />
            Gift Store
          </h1>
          <p className="text-muted-foreground">
            Redeem your points for amazing rewards
          </p>
        </div>
        <Card className="w-full sm:w-48 shrink-0">
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Your Points</div>
            <div className="text-2xl font-bold text-primary">{userPoints}</div>
          </CardContent>
        </Card>
      </div>

      {/* Gifts Grid */}
      {gifts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">No gifts available at the moment</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {gifts.map((gift) => {
            const canAfford = userPoints >= gift.pointsCost
            const inStock = gift.quantity > 0

            return (
              <Card key={gift.id} className={!inStock ? 'opacity-50' : ''}>
                <CardHeader>
                  {gift.imageUrl && (
                    <div className="w-full h-48 mb-4 rounded-md overflow-hidden bg-gray-100">
                      <img
                        src={gift.imageUrl}
                        alt={gift.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardTitle>{gift.name}</CardTitle>
                  {gift.description && (
                    <CardDescription>{gift.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Cost</span>
                    <span className="text-lg font-bold text-primary">
                      {gift.pointsCost} points
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Available</span>
                    <span className="text-sm font-semibold">
                      {gift.quantity} left
                    </span>
                  </div>
                  {gift.sponsor && (
                    <div className="pt-2 border-t">
                      <span className="text-xs text-muted-foreground">
                        Sponsored by {gift.sponsor.name}
                      </span>
                    </div>
                  )}
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full"
                    disabled={!canAfford || !inStock}
                    onClick={() => handleRedeemClick(gift)}
                  >
                    {!inStock
                      ? 'Out of Stock'
                      : !canAfford
                      ? 'Insufficient Points'
                      : 'Redeem'}
                  </Button>
                </CardFooter>
              </Card>
            )
          })}
        </div>
      )}

      {/* Redemption Modal */}
      {selectedGift && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Redeem {selectedGift.name}</CardTitle>
              <CardDescription>
                This will cost you {selectedGift.pointsCost} points
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Shipping Address</label>
                <textarea
                  className="w-full mt-2 p-3 border rounded-md resize-none"
                  rows="4"
                  placeholder="Enter your complete shipping address..."
                  value={shippingAddress}
                  onChange={(e) => setShippingAddress(e.target.value)}
                />
              </div>
              <div className="bg-muted p-3 rounded-md space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Current Points</span>
                  <span className="font-semibold">{userPoints}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Cost</span>
                  <span className="font-semibold text-destructive">
                    -{selectedGift.pointsCost}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t">
                  <span className="font-semibold">Remaining</span>
                  <span className="font-semibold text-primary">
                    {userPoints - selectedGift.pointsCost}
                  </span>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setSelectedGift(null)
                  setShippingAddress('')
                }}
                disabled={redeeming}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleRedeem}
                disabled={redeeming}
              >
                {redeeming ? 'Redeeming...' : 'Confirm Redemption'}
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </div>
  )
}
